/**
 * 全局Intersection Observer插件
 * 提供统一的元素可见性检测功能
 */

class IntersectionObserverPlugin {
  constructor(options = {}) {
    // 默认配置
    this.defaultOptions = {
      threshold: 0.2, // 元素10%可见时触发
      rootMargin: '0px',
      ...options
    };

    // 存储所有的观察器实例
    this.observers = new Map();
    // 存储所有被观察的元素及其回调
    this.elements = new Map();
  }

  /**
   * 初始化Intersection Observer
   * @param {Element|String} target - 要观察的元素或选择器
   * @param {Function} callback - 元素进入视口时的回调函数
   * @param {Object} options - Intersection Observer配置选项
   * @returns {String} observerId - 观察器ID，可用于后续操作
   */
  observe(target, callback, options = {}) {
    // 获取目标元素
    const element = typeof target === 'string' ? document.querySelector(target) : target;
    
    if (!element) {
      console.warn('IntersectionObserver: Target element not found', target);
      return null;
    }

    // 合并选项
    const observerOptions = { ...this.defaultOptions, ...options };
    
    // 创建唯一ID
    const observerId = this.generateId();
    
    // 创建观察器
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          // 调用回调函数
          callback(entry.target, entry);
          
          // 检查是否只需要执行一次
          if (options.once !== false) {
            // 执行一次后取消观察
            this.unobserve(observerId);
          }
        }
      });
    }, observerOptions);

    // 开始观察
    observer.observe(element);
    
    // 保存观察器实例
    this.observers.set(observerId, {
      observer,
      element,
      callback,
      options
    });

    return observerId;
  }

  /**
   * 停止观察特定元素
   * @param {String} observerId - 观察器ID
   */
  unobserve(observerId) {
    const observerData = this.observers.get(observerId);
    if (observerData) {
      observerData.observer.unobserve(observerData.element);
      observerData.observer.disconnect();
      this.observers.delete(observerId);
    }
  }

  /**
   * 停止所有观察
   */
  disconnectAll() {
    this.observers.forEach(data => {
      data.observer.disconnect();
    });
    this.observers.clear();
  }

  /**
   * 检查元素是否在视口中
   * @param {Element|String} target - 要检查的元素或选择器
   * @param {Number} threshold - 可见度阈值
   * @returns {Boolean} - 是否在视口中
   */
  isInViewport(target, threshold = 0.2) {
    const element = typeof target === 'string' ? document.querySelector(target) : target;
    if (!element) return false;

    const rect = element.getBoundingClientRect();
    const windowHeight = window.innerHeight || document.documentElement.clientHeight;
    const windowWidth = window.innerWidth || document.documentElement.clientWidth;

    const verticalVisible = rect.bottom >= 0 && rect.top <= windowHeight;
    const horizontalVisible = rect.right >= 0 && rect.left <= windowWidth;

    if (!verticalVisible || !horizontalVisible) return false;

    // 计算可见面积
    const visibleHeight = Math.min(
      Math.max(windowHeight - rect.top, 0),
      rect.height
    );
    const visibleWidth = Math.min(
      Math.max(windowWidth - rect.left, 0),
      rect.width
    );

    const visibleArea = visibleHeight * visibleWidth;
    const totalArea = rect.width * rect.height;
    const visibilityRatio = visibleArea / totalArea;

    return visibilityRatio >= threshold;
  }

  /**
   * 生成唯一ID
   * @returns {String} - 唯一ID
   */
  generateId() {
    return Date.now().toString(36) + Math.random().toString(36).substr(2, 5);
  }
}

// 创建全局实例
const plugin = {
  install(Vue, options = {}) {
    const observerPlugin = new IntersectionObserverPlugin(options);
    
    // 添加到Vue原型上，使其在所有组件中可用
    Vue.prototype.$intersectionObserver = observerPlugin;
    
    // 注册一个全局指令
    Vue.directive('observe', {
      bind(el, binding) {
        // 获取参数
        const callback = binding.value;
        const options = binding.arg ? { ...binding.arg } : {};
        
        // 保存observer ID到元素上
        const observerId = observerPlugin.observe(el, callback, options);
        el._io_observerId = observerId;
      },
      unbind(el) {
        if (el._io_observerId) {
          observerPlugin.unobserve(el._io_observerId);
          el._io_observerId = null;
        }
      }
    });
  }
};

// 导出插件
export default plugin;

// 也可以导出类，以便在需要时单独实例化
export { IntersectionObserverPlugin };